/** تجهيز محدود لإطارَي النموذج التالي كي يبدو تبديل GFS/ECMWF/ICON فورياً. */

import type { ForecastGridType } from '../config/weatherLayers';
import type { WeatherModelId } from '../store/types';
import { queuePrefetchImage } from './prefetchQueue';
import { fetchRasterMeta, rasterDirFor } from './modelRunInfo';
import { getLayerRasterSource } from './rasterVersion';

interface RasterMeta { run_epoch?: number; hours?: number; layers?: string[] }

const metaCache = new Map<string, Promise<RasterMeta | null>>();
const queued = new Set<string>();

const dirFor = rasterDirFor;

function getMeta(dir: string): Promise<RasterMeta | null> {
    const existing = metaCache.get(dir);
    if (existing) return existing;
    const task = fetchRasterMeta(dir) as Promise<RasterMeta | null>;
    metaCache.set(dir, task);
    return task;
}

function queue(src: string, priority: 'urgent' | 'background'): void {
    if (queued.has(src)) return;
    queued.add(src);
    if (queued.size > 120) queued.clear();
    queuePrefetchImage(src, priority);
}

/**
 * يجهز إطارات النموذج المقصود حول **اللحظة المعروضة**. لا يغيّر النموذج المختار أو
 * الـ timeline.
 *
 * ── لماذا الزمن المطلق لا الفهرس ────────────────────────────────────────────────
 *
 * كان يُمرَّر `currentTimeIndex` ويُستعمل كما هو على مجلّد النموذج الآخر. لكن الفهرس
 * إزاحةٌ بالساعات **من دورة النموذج الحالي**، وكل نموذج يبدأ دورته في ساعة مختلفة
 * (‏GFS كل 6 ساعات، ECMWF كل 12، وبينهما تأخّر نشر متفاوت). فالفهرس 27 تحت GFS قد يوافق
 * الفهرس 21 تحت ECMWF — والإحماء كان يجلب `..._027.png` من ECMWF: ملفّ موجود لكن لساعة
 * أخرى، لا أحد سيطلبه بعد التبديل. النتيجة: بيانات تُنزَّل بلا فائدة، والتبديل يبقى
 * ينتظر الشبكة كأن لا إحماء أصلاً — وهو ما يُحسّ «بطئاً في التنقّل بين النماذج».
 *
 * الآن نمرّر اللحظة المعروضة بالثواني ونحوّلها إلى فهرس **داخل دورة النموذج الهدف**
 * باستعمال `run_epoch` القادم من meta الخاصة به (نجلبها هنا أصلاً).
 *
 * نافذة ±2: التبديل يقع غالباً أثناء التصفّح لا التشغيل، والمستخدم قد يخطو خطوةً أو
 * اثنتين بعده مباشرةً؛ خمسة إطارات تكفي ولا تُثقل الشبكة قبل أن يقرّر التبديل فعلاً.
 */
export async function warmModelFrames(
    model: WeatherModelId,
    /** النموذج المعروض الآن وفهرس إطاره — منهما نشتقّ اللحظة المطلقة. */
    fromModel: WeatherModelId,
    fromIndex: number,
    scalarType: ForecastGridType | null,
    windVisible: boolean,
    particlesEnabled: boolean,
    /**
     * `'hover'`: المستخدم لمس زرّ النموذج أو مرّ عليه — تبديلٌ وشيك، فالأولوية عاجلة
     *   ونافذة ±2 كي يبدو التبديل فورياً.
     * `'idle'` (الافتراضي): تجهيز استباقي لنموذج **لا يُنظر إليه**. أولوية خلفية
     *   ونافذة ±1.
     *
     * ── لماذا الفصل ──────────────────────────────────────────────────────────
     * كان كل الإحماء يدخل الصفّ **العاجل** — نفس الصفّ الذي ينتظر فيه الإطارُ الذي
     * يحدّق فيه الزائر الآن. وقياس على الموقع الحيّ (2026-09-02) أظهر الأثر: من 18.91
     * ميغابايت في أول تحميل، **6.55 لنموذج ECMWF لا يراه أحد** (16 طلباً)، تتقدّم
     * صفّاً على إطارات النموذج المعروض. أي أن الصورة الأولى تنتظر بيانات نموذجٍ قد
     * لا يُفتح في الجلسة كلّها.
     *
     * الصفّ الخلفي يجلب **البايتات فقط** بلا فكّ — وهو كل ما يلزم لجعل التبديل
     * سريعاً: الفكّ من بايتات محفوظة أجزاء من الثانية، أمّا رحلة الشبكة فهي الثمن
     * الحقيقي. فيبقى التبديل فورياً بلا أن يزاحم أحدٌ الإطار المعروض.
     */
    intent: 'hover' | 'idle' = 'idle',
): Promise<void> {
    // اللحظة المعروضة = دورة النموذج الحالي + إزاحة فهرسه. تُشتقّ هنا لا عند المستدعي:
    // meta النموذجين مخزّنة في هذا الملفّ أصلاً، فلا رحلة شبكة إضافية.
    const fromMeta = await getMeta(dirFor(fromModel));
    const fromRun = Number(fromMeta?.run_epoch);
    if (!Number.isFinite(fromRun) || fromRun <= 0) return;
    const displayedEpochSec = fromRun + fromIndex * 3600;

    const dir = dirFor(model);
    const meta = await getMeta(dir);
    if (!meta) return;
    const version = Number(meta.run_epoch);
    const suffix = Number.isFinite(version) && version > 0 ? `?v=${version}` : '';
    const hasWindSpeed = meta.layers?.includes('wind-speed') ?? false;

    // الفهرس داخل دورة النموذج الهدف. بلا run_epoch صالح لا يمكن الربط، فلا نُحمّي شيئاً
    // بدل تحميل ساعات عشوائية.
    if (!Number.isFinite(version) || version <= 0) return;
    const baseHour = Math.round((displayedEpochSec - version) / 3600);
    if (!Number.isFinite(baseHour)) return;

    /*
     * **الحدّ الأعلى إلزامي كالأدنى.**
     *
     * كل نموذج يبدأ دورته في ساعة مختلفة، فاللحظة نفسها تقع عند فهرس مختلف لكل نموذج —
     * وقد تقع **بعد نهاية** مدى النموذج الهدف. رُصد في الإنتاج: طلبات لـ`icon/wind_125`
     * و`_126` و`_127` بينما `meta.hours = 121` (المدى الصالح 0..120)، أي ملفّات لم تُولَّد
     * قطّ. حرستُ `hour < 0` وأغفلتُ الطرف الآخر، فصار الإحماء يُنتج 404 بدل أن يُسرّع.
     *
     * `hours` يصل مع meta التي نجلبها هنا أصلاً — لا كلفة إضافية للفحص.
     */
    const maxHours = Number(meta.hours);
    const lastHour = Number.isFinite(maxHours) && maxHours > 0 ? maxHours - 1 : Infinity;

    const files = new Set<string>();
    // نافذة التجهيز الخامل أضيق: ثلاثة إطارات تكفي ليبدأ التبديل فوراً، والبقيّة
    // تصل بعد اللمس. خمسة إطارات × نوعين لكل نموذج غير معروض ثمنٌ يدفعه كل زائر مرّة،
    // ويستفيد منه من يبدّل فعلاً — وهم قلّة.
    const offsets = intent === 'hover' ? [0, 1, -1, 2, -2] : [0, 1, -1];
    const priority = intent === 'hover' ? 'urgent' : 'background';
    for (const offset of offsets) {
        const hour = baseHour + offset;
        if (hour < 0 || hour > lastHour) continue;
        const hhh = String(hour).padStart(3, '0');
        // الطبقة السلَّمية عبر السجلّ: قد تكون طبقة رصد مستقلّة عن هذا النموذج تماماً،
        // فمحورها ليس ساعات هذه الدورة ولا ملفّاتها في مجلّده. مع تسجيلٍ قائم نتخطّاها
        // هنا — إحماؤها من مسؤولية مؤثّر الإحماء الذي يعرف محورها الصحيح.
        if (scalarType && !getLayerRasterSource(scalarType)) files.add(`${scalarType}_${hhh}.png`);
        if (windVisible) files.add(`${hasWindSpeed ? 'wind-speed' : 'wind'}_${hhh}.png`);
        if (particlesEnabled) files.add(`wind_${hhh}.png`);
    }
    for (const file of files) queue(`${import.meta.env.BASE_URL}${dir}${file}${suffix}`, priority);
}
